'use client'

import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { HandCoins, X, Check, Copy } from 'lucide-react'
import { formatPaise } from '../../../lib/money'

/* Donation Session Engine — public donate flow (Phase 2).
   Rendered ONLY inside the profile's verified+upi_id display block AND when the donationEngine
   flag is ON. Money never touches the platform: the donor pays the masjid directly via their UPI
   app; we only record a CLAIM the masjid later confirms. No "donation successful", no receipt,
   no fabricated confirmation — nothing here is shown as a masjid/campaign total. */

type Step = 'amount' | 'details' | 'pay' | 'result'
type ResultKind = 'confirmed' | 'failed' | null

const PRESETS = [51, 101, 501]
const GREEN = '#0E7C61'

function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return (crypto as any).randomUUID()
  } catch { /* fall through */ }
  return `k-${Date.now()}-${Math.round(Math.random() * 1e9)}`
}

export default function DonateFlow({ masjidId, masjidName }: { masjidId: string; masjidName: string }) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('amount')
  const [amount, setAmount] = useState('')
  const [name, setName] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [dua, setDua] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [whatsappOptin, setWhatsappOptin] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [session, setSession] = useState<{ reference: string; upiIntentUrl: string; amountPaise: number; receiptToken: string } | null>(null)
  const [result, setResult] = useState<ResultKind>(null)
  const [copied, setCopied] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const idemKey = useRef<string>('')

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsMobile(/android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent))
    }
  }, [])

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  function reset() {
    setStep('amount'); setError(''); setSession(null); setResult(null); setSubmitting(false); setCopied(false)
  }
  function close() { setOpen(false) }
  function startAgain() { setSession(null); setResult(null); setError(''); setStep('amount') }

  function goToDetails() {
    setError('')
    const n = Number(String(amount).replace(/[₹,\s]/g, ''))
    if (!amount || !Number.isFinite(n) || n <= 0) { setError('Enter an amount greater than zero.'); return }
    idemKey.current = newIdempotencyKey()
    setStep('details')
  }

  async function submit() {
    setError('')
    if (!name.trim()) { setError('Please enter your name.'); return }
    if (mobile.replace(/\D/g, '').length < 7) { setError('Please enter a valid mobile number.'); return }
    setSubmitting(true)
    try {
      const res = await fetch('/api/donations/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idemKey.current },
        body: JSON.stringify({
          masjidId,
          amountRupees: amount,
          donorName: name.trim(),
          donorMobile: mobile.trim(),
          donorEmail: email.trim() || undefined,
          message: dua.trim() || undefined,
          isAnonymous: anonymous,
          whatsappOptin,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setError(data?.error || 'Could not start the donation. Please try again.'); setSubmitting(false); return }
      setSession({ reference: data.reference, upiIntentUrl: data.upiIntentUrl, amountPaise: data.amountPaise, receiptToken: data.receiptToken })
      setStep('pay')
      // On mobile, proactively deep-link the UPI app.
      if (isMobile && data.upiIntentUrl) {
        try { window.location.href = data.upiIntentUrl } catch { /* user can tap the button */ }
      }
    } catch {
      setError('Network error. Please try again.')
    }
    setSubmitting(false)
  }

  async function report(kind: 'confirm' | 'failed') {
    if (!session) return
    setSubmitting(true); setError('')
    try {
      // H-10: send the high-entropy receipt token (from the create response) so the server can bind
      // this confirm/failed action to the donor who created the session — reference alone is enumerable.
      const res = await fetch(`/api/donations/session/${encodeURIComponent(session.reference)}/${kind}?token=${encodeURIComponent(session.receiptToken)}`, { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok && res.status !== 409) { setError(data?.error || 'Something went wrong. Please try again.'); setSubmitting(false); return }
      setResult(kind === 'confirm' ? 'confirmed' : 'failed')
      setStep('result')
    } catch {
      setError('Network error. Please try again.')
    }
    setSubmitting(false)
  }

  function copyRef() {
    if (!session) return
    try { navigator.clipboard?.writeText(session.reference); setCopied(true); setTimeout(() => setCopied(false), 1800) } catch { /* ignore */ }
  }

  const btnBase: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    borderRadius: 12, fontWeight: 800, cursor: 'pointer', border: 'none', width: '100%',
    minHeight: 52, fontSize: 17, padding: '14px 18px',
  }
  const primaryBtn: React.CSSProperties = { ...btnBase, background: GREEN, color: '#fff' }
  const ghostBtn: React.CSSProperties = { ...btnBase, background: '#eef4f2', color: '#0b132b' }
  const inputStyle: React.CSSProperties = {
    width: '100%', minHeight: 52, borderRadius: 12, border: '1.5px solid #d5e0dc',
    padding: '12px 14px', fontSize: 16, color: '#0b132b', outline: 'none', boxSizing: 'border-box',
  }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 6px' }

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true) }}
        style={{ ...primaryBtn, width: 'auto', minHeight: 46, fontSize: 15, marginBottom: 10 }}
      >
        <HandCoins size={18} /> Donate to this masjid
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Donate to ${masjidName}`}
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(11,19,43,0.55)', zIndex: 1000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', width: '100%', maxWidth: 460, maxHeight: '92vh', overflowY: 'auto',
              borderRadius: '20px 20px 0 0', padding: '20px 20px 28px', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: GREEN }}>Donate directly via UPI</p>
                <p style={{ margin: '2px 0 0', fontSize: 17, fontWeight: 800, color: '#0b132b' }}>{masjidName}</p>
              </div>
              <button type="button" aria-label="Close" onClick={close} style={{ background: '#f1f5f4', border: 'none', borderRadius: 10, width: 40, height: 40, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={20} color="#475569" />
              </button>
            </div>

            {/* STEP: AMOUNT */}
            {step === 'amount' && (
              <div>
                <label style={labelStyle}>Choose an amount</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {PRESETS.map((p) => {
                    const active = String(p) === String(amount).replace(/[₹,\s]/g, '')
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setAmount(String(p))}
                        style={{
                          minHeight: 56, borderRadius: 12, fontSize: 18, fontWeight: 800, cursor: 'pointer',
                          border: active ? `2px solid ${GREEN}` : '1.5px solid #d5e0dc',
                          background: active ? '#e8f5f0' : '#fff', color: '#0b132b',
                        }}
                      >₹{p}</button>
                    )
                  })}
                </div>
                <label style={labelStyle}>Or enter an amount (₹)</label>
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 251"
                  style={{ ...inputStyle, marginBottom: 14 }}
                />
                {error && <p style={{ color: '#b91c1c', fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>{error}</p>}
                <button type="button" style={primaryBtn} onClick={goToDetails}>Continue</button>
                <p style={{ margin: '12px 0 0', fontSize: 12.5, color: '#6b7a86', fontWeight: 600, lineHeight: 1.5 }}>
                  Your payment goes straight to the masjid's own UPI. Masjids.in never collects or holds the money.
                </p>
              </div>
            )}

            {/* STEP: DETAILS */}
            {step === 'details' && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Your name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={inputStyle} autoComplete="name" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Mobile number</label>
                  <input value={mobile} onChange={(e) => setMobile(e.target.value)} inputMode="tel" placeholder="10-digit mobile" style={inputStyle} autoComplete="tel" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Email <span style={{ color: '#94a3b8', fontWeight: 600 }}>(optional)</span></label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" placeholder="you@example.com" style={inputStyle} autoComplete="email" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Dua / message <span style={{ color: '#94a3b8', fontWeight: 600 }}>(optional)</span></label>
                  <textarea value={dua} onChange={(e) => setDua(e.target.value)} placeholder="A short dua or note" rows={2} style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} />
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#334155', fontWeight: 600 }}>Keep my name private (donate anonymously)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
                  <input type="checkbox" checked={whatsappOptin} onChange={(e) => setWhatsappOptin(e.target.checked)} style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#334155', fontWeight: 600 }}>Send my receipt on WhatsApp when available</span>
                </label>
                {error && <p style={{ color: '#b91c1c', fontSize: 14, fontWeight: 600, margin: '0 0 12px' }}>{error}</p>}
                <button type="button" style={{ ...primaryBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={submit}>
                  {submitting ? 'Starting…' : 'Continue to pay'}
                </button>
                <button type="button" style={{ ...ghostBtn, marginTop: 10 }} onClick={() => { setError(''); setStep('amount') }}>Back</button>
              </div>
            )}

            {/* STEP: PAY */}
            {step === 'pay' && session && (
              <div>
                <p style={{ margin: '0 0 4px', fontSize: 14, color: '#475569', fontWeight: 600 }}>You're donating</p>
                <p style={{ margin: '0 0 16px', fontSize: 30, fontWeight: 900, color: '#0b132b' }}>{formatPaise(session.amountPaise)}</p>

                {/* Reference — shown on-screen AND carried in the UPI note (tn=). */}
                <div style={{ background: '#f0f7f4', border: `1.5px dashed ${GREEN}`, borderRadius: 14, padding: '12px 14px', marginBottom: 16 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your reference</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 24, fontWeight: 900, color: '#0b132b', letterSpacing: '0.04em' }}>{session.reference}</span>
                    <button type="button" onClick={copyRef} aria-label="Copy reference" style={{ background: '#fff', border: '1px solid #cfe0da', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: GREEN }}>
                      {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <p style={{ margin: '6px 0 0', fontSize: 12.5, color: '#475569', fontWeight: 600 }}>The masjid uses this reference to match your donation to their records.</p>
                </div>

                {isMobile ? (
                  <>
                    <a href={session.upiIntentUrl} style={{ ...primaryBtn, textDecoration: 'none' }}>Open my UPI app to pay</a>
                    <div style={{ textAlign: 'center', margin: '16px 0 8px' }}>
                      <p style={{ margin: '0 0 8px', fontSize: 12.5, color: '#6b7a86', fontWeight: 600 }}>On a computer? Scan this with your phone:</p>
                      <div style={{ display: 'inline-block', background: '#fff', padding: 10, borderRadius: 12, border: '1px solid #e6efec' }}>
                        <QRCodeSVG value={session.upiIntentUrl} size={128} level="M" />
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'inline-block', background: '#fff', padding: 12, borderRadius: 14, border: '1px solid #e6efec' }}>
                      <QRCodeSVG value={session.upiIntentUrl} size={176} level="M" />
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 14, color: '#334155', fontWeight: 700 }}>Scan with your phone's UPI app (GPay / PhonePe / Paytm)</p>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #eef2f1', margin: '18px 0 14px' }} />
                <p style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#0b132b', textAlign: 'center' }}>Did the payment complete?</p>
                {error && <p style={{ color: '#b91c1c', fontSize: 14, fontWeight: 600, margin: '0 0 10px', textAlign: 'center' }}>{error}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <button type="button" style={{ ...primaryBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting} onClick={() => report('confirm')}>Yes, I paid</button>
                  <button type="button" style={{ ...ghostBtn }} disabled={submitting} onClick={() => report('failed')}>Payment failed</button>
                </div>
              </div>
            )}

            {/* STEP: RESULT */}
            {step === 'result' && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                {result === 'confirmed' ? (
                  <>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8f5f0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <Check size={34} color={GREEN} />
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 900, color: '#0b132b' }}>JazakAllahu Khairan</p>
                    <p style={{ margin: '0 auto 6px', fontSize: 15, color: '#334155', fontWeight: 600, lineHeight: 1.55, maxWidth: 340 }}>
                      Your donation is recorded. The masjid will confirm it against their own records.
                    </p>
                    {session && (
                      <p style={{ margin: '0 0 14px', fontSize: 13.5, color: '#6b7a86', fontWeight: 700 }}>Reference: {session.reference}</p>
                    )}
                    {session?.receiptToken && (
                      <a
                        href={`/donations/receipt/${encodeURIComponent(session.receiptToken)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...ghostBtn, textDecoration: 'none', marginBottom: 10 }}
                      >
                        View &amp; save your acknowledgment
                      </a>
                    )}
                    <p style={{ margin: '0 auto 18px', fontSize: 12.5, color: '#94a3b8', fontWeight: 600, lineHeight: 1.5, maxWidth: 340 }}>
                      Save that link — once the masjid confirms your donation against their records, your confirmed acknowledgment appears there. This is a record, not a receipt; Masjids.in did not process the payment.
                    </p>
                    <button type="button" style={primaryBtn} onClick={close}>Done</button>
                  </>
                ) : (
                  <>
                    <p style={{ margin: '10px 0 8px', fontSize: 20, fontWeight: 900, color: '#0b132b' }}>No problem</p>
                    <p style={{ margin: '0 auto 18px', fontSize: 15, color: '#334155', fontWeight: 600, lineHeight: 1.55, maxWidth: 340 }}>
                      Nothing was recorded as paid. You can start a new donation whenever you're ready.
                    </p>
                    <button type="button" style={primaryBtn} onClick={startAgain}>Start again</button>
                    <button type="button" style={{ ...ghostBtn, marginTop: 10 }} onClick={close}>Close</button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
